import { motion } from 'framer-motion';
import { gaugeFill } from '@/lib/animations';

interface GaugeProps {
  title: string;
  value: number;
  unit?: string;
  min: number;
  max: number;
  limit?: number;
  size?: 'sm' | 'md' | 'lg';
  delay?: number;
}

export default function Gauge({ title, value, unit = '', min, max, limit, size = 'md', delay = 0 }: GaugeProps) {
  const percentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const limitPercentage = limit != null ? Math.min(100, Math.max(0, ((limit - min) / (max - min)) * 100)) : null;

  const sizeMap = { sm: { w: 120, h: 64, stroke: 8 }, md: { w: 160, h: 86, stroke: 10 }, lg: { w: 200, h: 108, stroke: 12 } };
  const { w, h, stroke } = sizeMap[size];

  const radius = (w - stroke) / 2;
  const cx = w / 2;
  const cy = h - stroke / 2;
  const arcLength = Math.PI * radius;

  // Color based on percentage
  const color = percentage > 80 ? '#ef4444' : percentage > 50 ? '#f59e0b' : '#10b981';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="flex flex-col items-center"
    >
      <svg width={w} height={h + 24} viewBox={`0 0 ${w} ${h + 24}`}>
        {/* Background arc */}
        <path
          d={`M ${stroke / 2} ${cy} A ${radius} ${radius} 0 0 1 ${w - stroke / 2} ${cy}`}
          fill="none"
          stroke="#1e293b"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <motion.path
          d={`M ${stroke / 2} ${cy} A ${radius} ${radius} 0 0 1 ${w - stroke / 2} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arcLength}`}
          variants={gaugeFill}
          initial="hidden"
          animate="visible"
          transition={{ delay: delay + 0.2, duration: 0.8, ease: 'easeOut' }}
          style={{
            strokeDashoffset: arcLength * (1 - percentage / 100),
            filter: `drop-shadow(0 0 4px ${color}40)`,
          }}
        />
        {/* Limit marker */}
        {limitPercentage != null && (
          <line
            x1={cx - Math.cos((limitPercentage / 100) * Math.PI) * (radius + 4)}
            y1={cy - Math.sin((limitPercentage / 100) * Math.PI) * (radius + 4)}
            x2={cx - Math.cos((limitPercentage / 100) * Math.PI) * (radius - stroke - 4)}
            y2={cy - Math.sin((limitPercentage / 100) * Math.PI) * (radius - stroke - 4)}
            stroke="#94a3b8"
            strokeWidth="2"
          />
        )}
        {/* Value label */}
        <text x={cx} y={cy + 4} textAnchor="middle" className="fill-white" style={{ fontSize: size === 'sm' ? '14px' : size === 'md' ? '16px' : '20px', fontWeight: 'bold' }}>
          {value}
        </text>
        {/* Unit */}
        <text x={cx} y={cy + 18} textAnchor="middle" className="fill-slate-500" style={{ fontSize: 'var(--rb-font-xxs)' }}>
          {unit}
        </text>
      </svg>
      <p className="text-xs text-slate-400 mt-1">{title}</p>
    </motion.div>
  );
}
